<script setup lang="ts">
import { ref, watch, nextTick } from 'vue'
import { X, ChevronUp } from 'lucide-vue-next'

interface Props {
  show: boolean
  title: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'close'): void
}>()

const scrollContainer = ref<HTMLElement | null>(null)
const showScrollTop = ref(false)
const scrollProgress = ref(0)

function closeModal() {
  emit('close')
}

function handleScroll(e: Event) {
  const el = e.target as HTMLElement
  const scrollTop = el.scrollTop
  const scrollHeight = el.scrollHeight - el.clientHeight
  showScrollTop.value = scrollTop > 300
  scrollProgress.value = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0
}

function scrollToTop() {
  scrollContainer.value?.scrollTo({ top: 0, behavior: 'smooth' })
}

watch(() => props.show, async (newVal) => {
  if (newVal) {
    scrollProgress.value = 0
    showScrollTop.value = false
    await nextTick()
    scrollContainer.value?.scrollTo({ top: 0 })
  }
})
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="show"
        class="fixed inset-0 z-[60] overflow-y-auto"
        @keydown.escape="closeModal"
      >
        <!-- Backdrop -->
        <div
          class="fixed inset-0 bg-black/60 backdrop-blur-md"
          @click="closeModal"
        />

        <!-- Modal -->
        <div class="flex min-h-full items-center justify-center p-4">
          <Transition
            enter-active-class="transition ease-out duration-300"
            enter-from-class="opacity-0 scale-95 translate-y-4"
            enter-to-class="opacity-100 scale-100 translate-y-0"
            leave-active-class="transition ease-in duration-200"
            leave-from-class="opacity-100 scale-100 translate-y-0"
            leave-to-class="opacity-0 scale-95 translate-y-4"
          >
            <div
              v-if="show"
              class="relative w-full max-w-2xl max-h-[80vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
              @click.stop
            >
              <!-- Progress bar -->
              <div class="absolute top-0 left-0 right-0 h-0.5 bg-gray-100 z-10">
                <div
                  class="h-full bg-green-500 transition-all duration-150 ease-out"
                  :style="{ width: scrollProgress + '%' }"
                />
              </div>

              <!-- Close button -->
              <button
                @click="closeModal"
                class="absolute top-4 right-4 z-10 p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100/80 rounded-full transition-colors"
              >
                <X class="w-5 h-5" />
              </button>

              <!-- Scrollable Content -->
              <div
                ref="scrollContainer"
                class="flex-1 overflow-y-auto overscroll-contain px-6 sm:px-8 pt-8 pb-8"
                @scroll="handleScroll"
              >
                <h2 class="text-2xl font-bold text-gray-900 mb-6 pr-10">{{ title }}</h2>
                <slot />
              </div>

              <!-- Scroll to top -->
              <Transition
                enter-active-class="transition ease-out duration-200"
                enter-from-class="opacity-0 translate-y-4"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition ease-in duration-150"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-4"
              >
                <button
                  v-if="showScrollTop"
                  @click="scrollToTop"
                  class="absolute bottom-6 right-6 p-3 bg-green-600 text-white rounded-full shadow-lg hover:bg-green-700 transition-colors"
                >
                  <ChevronUp class="w-5 h-5" />
                </button>
              </Transition>
            </div>
          </Transition>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
